import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { todayIstIso } from "@/lib/tz";

type Slot = { start_time: string; end_time: string; source: string; row_id: string };

function useCurrentUserId() {
  const [id, setId] = useState<string | undefined>();
  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => setId(data.user?.id));
  }, []);
  return id;
}

/** Fetches today's occupied time ranges for the current user across every department's tables. */
export function useOccupiedSlots(userId: string | undefined) {
  const today = todayIstIso();
  return useQuery({
    queryKey: ["occupied-slots", userId, today],
    enabled: !!userId,
    staleTime: 30_000,
    queryFn: async (): Promise<Slot[]> => {
      const { data, error } = await supabase.rpc("get_occupied_slots", {
        _user_id: userId!,
        _date: today,
      });
      if (error) throw error;
      return (data ?? []) as Slot[];
    },
  });
}

const fmt = (t: string) => t.slice(0, 5);

/**
 * Inline helper text shown under time pickers, e.g.
 *   Occupied today: 10:00–12:00, 14:00–15:00
 * Pass excludeId/excludeSource to skip the row currently being edited.
 */
export function OccupiedSlotsHint({
  excludeId,
  excludeSource,
}: {
  excludeId?: string;
  excludeSource?: string;
}) {
  const userId = useCurrentUserId();
  const { data } = useOccupiedSlots(userId);
  const slots = (data ?? []).filter(
    (s) => !(excludeId && excludeSource && s.row_id === excludeId && s.source === excludeSource),
  );
  if (slots.length === 0) return null;
  const text = slots
    .slice()
    .sort((a, b) => a.start_time.localeCompare(b.start_time))
    .map((s) => `${fmt(s.start_time)}–${fmt(s.end_time)}`)
    .join(", ");
  return (
    <p className="text-[11px] text-muted-foreground">
      Occupied today: <span className="font-medium text-foreground/80">{text}</span>
    </p>
  );
}
